/**
 * The country dial codes offered when enrolling a phone as a second factor.
 *
 * WHY A LIST AND NOT A FIELD. The number has to reach Firebase in E.164
 * (`+15551234567`), and the commonest way it does not is the user typing
 * `555 123 4567` — their own number, the way they would say it out loud. The
 * screen used to answer that with a hint under the field ("Include the country
 * code"), which is a rule to remember rather than a mistake made impossible.
 * Picked from a list, the code cannot be forgotten, because it was never typed.
 *
 * WHY NOT ALL 179. There is already a country list in this codebase —
 * features/Clients/countries.ts, the countries IBKR accepts — and it carries no
 * dial codes, so a full picker means inventing and then maintaining a second
 * list of 179. It would also be the heaviest control on a screen whose whole
 * job is "type your number", scrolled past by everyone to reach the two entries
 * at the top. This is a curated starting set, ordered with the custody markets
 * first, and it is one array to extend.
 *
 * NOT unique by code on purpose: +1 appears twice. Canada and the United States
 * share it, and a Canadian advisor looking for their country should find it
 * rather than have to know that "United States" also means them.
 */
export interface DialCode {
  /** ISO 3166-1 alpha-2, used as the option value — `code` is not unique. */
  iso: string;
  country: string;
  /** Digits only, no plus. */
  dial: string;
  /**
   * A local mobile number in that country's own notation, for the placeholder.
   *
   * National formats do not resemble each other — a UK mobile is 7700 900123,
   * a French one 6 12 34 56 78, an Emirati one 50 123 4567 — so a single
   * example is wrong everywhere except where it came from. `555 123 4567` was
   * shown under +971, which tells a user in Dubai to enter a number in a shape
   * their own has never had.
   *
   * Written WITHOUT the trunk prefix, because the country code is already in
   * front of it and the two do not go together: a British number written 07700
   * everywhere domestically is 7700 here. toE164 strips a leading zero for the
   * same reason, so the placeholder shows the shape the field actually wants.
   *
   * Canada, the US and the UK use their official fictional ranges (NANP 555,
   * Ofcom's 07700 900xxx). The rest are plausible mobile prefixes rather than
   * reserved ones — nothing dials a placeholder, and the point is the shape.
   */
  example: string;
}

export const DIAL_CODES: Array<DialCode> = [
  { iso: 'CA', country: 'Canada', dial: '1', example: '555 123 4567' },
  { iso: 'US', country: 'United States', dial: '1', example: '555 123 4567' },
  { iso: 'GB', country: 'United Kingdom', dial: '44', example: '7700 900123' },
  { iso: 'IE', country: 'Ireland', dial: '353', example: '85 123 4567' },
  { iso: 'FR', country: 'France', dial: '33', example: '6 12 34 56 78' },
  { iso: 'DE', country: 'Germany', dial: '49', example: '151 23456789' },
  { iso: 'CH', country: 'Switzerland', dial: '41', example: '78 123 45 67' },
  { iso: 'LU', country: 'Luxembourg', dial: '352', example: '621 123 456' },
  {
    iso: 'AE',
    country: 'United Arab Emirates',
    dial: '971',
    example: '50 123 4567',
  },
  { iso: 'IL', country: 'Israel', dial: '972', example: '50 123 4567' },
  { iso: 'IN', country: 'India', dial: '91', example: '98765 43210' },
  { iso: 'SG', country: 'Singapore', dial: '65', example: '8123 4567' },
  { iso: 'HK', country: 'Hong Kong', dial: '852', example: '5123 4567' },
  { iso: 'AU', country: 'Australia', dial: '61', example: '412 345 678' },
  { iso: 'JP', country: 'Japan', dial: '81', example: '90 1234 5678' },
  { iso: 'MX', country: 'Mexico', dial: '52', example: '55 1234 5678' },
  { iso: 'BR', country: 'Brazil', dial: '55', example: '11 91234 5678' },
];

/** Canada — the platform's home market, and the sensible default. */
export const DEFAULT_DIAL_ISO = 'CA';

/** The placeholder for an ISO code, falling back to the default's. */
export function exampleFor(iso: string): string {
  return (
    DIAL_CODES.find((c) => c.iso === iso)?.example ??
    DIAL_CODES.find((c) => c.iso === DEFAULT_DIAL_ISO)!.example
  );
}

/** The country name for an ISO code, falling back to the default's. */
export function countryFor(iso: string): string {
  return (
    DIAL_CODES.find((c) => c.iso === iso)?.country ??
    DIAL_CODES.find((c) => c.iso === DEFAULT_DIAL_ISO)!.country
  );
}

/** The dial digits for an ISO code, falling back to the default's. */
export function dialFor(iso: string): string {
  return (
    DIAL_CODES.find((c) => c.iso === iso)?.dial ??
    DIAL_CODES.find((c) => c.iso === DEFAULT_DIAL_ISO)!.dial
  );
}

/**
 * Compose what Firebase is given, from the two halves the user filled in.
 *
 * Everything that is not a digit is dropped from the local part: people type
 * their number with spaces, dashes and brackets, and all three are formatting
 * rather than content. A leading zero goes too — it is a trunk prefix, correct
 * when dialling inside the country and wrong the moment a country code is in
 * front of it, which is a mistake nobody makes on purpose and everybody makes.
 */
export function toE164(dial: string, localNumber: string): string {
  const digits = localNumber.replace(/\D/g, '').replace(/^0+/, '');
  return `+${dial}${digits}`;
}

/**
 * The local number, grouped the way its country writes it.
 *
 * WITHOUT THE DIAL CODE, because the field that shows this already prints
 * `+1` beside it — see `PhoneField`. `displayPhone` is this plus the code, for
 * the places that render a number as one string.
 *
 * GROUPED AS IT IS TYPED, not once it is complete. A number that stays a run
 * of digits until the tenth and then rearranges itself is a jump under the
 * cursor; grouping from the fourth digit means the shape only ever grows.
 *
 * NANP IS `NNN NNN-NNNN` and everywhere else is threes. That is not a claim
 * that every country groups in threes — it is that a wrong-but-even grouping
 * of a foreign number reads as a phone number, and one unbroken run of digits
 * does not. The dial codes this list carries are the ones the form needs.
 */
export function nationalPhone(iso: string, localNumber: string): string {
  const digits = (localNumber ?? '').replace(/\D/g, '');
  if (!digits) return '';
  const dial = dialFor(iso) || dialFor(DEFAULT_DIAL_ISO);
  if (dial === '1') {
    const ten = digits.slice(0, 10);
    if (ten.length <= 3) return ten;
    if (ten.length <= 6) return `${ten.slice(0, 3)} ${ten.slice(3)}`;
    return `${ten.slice(0, 3)} ${ten.slice(3, 6)}-${ten.slice(6)}`;
  }
  return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
}

/**
 * A number as a person writes it, for a row that is read rather than typed in.
 *
 * `+1 438 373-7501`, not `4383737501`. Ten bare digits is a string; the same
 * ten grouped is a phone number, and the country code says which country's
 * number it is — which on a dealer's screen is the difference between a
 * Canadian client and one the firm has to treat as foreign.
 *
 * NANP IS FORMATTED PROPERLY AND THE REST IS GROUPED. Canada and the United
 * States are the two markets this product clears in, their plan is one shape —
 * three, three, four — and it is the only one worth encoding by hand. Doing the
 * same for every country means libphonenumber, which is a dependency this
 * screen does not carry for one label; everywhere else gets the dial code and
 * the digits in threes, which is readable without claiming a national
 * convention we have not checked.
 *
 * Returns the input untouched when it cannot be improved: a half-typed number
 * is still the number somebody typed, and a formatter that swallows it is
 * worse than one that does nothing.
 */
export function displayPhone(iso: string, localNumber: string): string {
  const digits = (localNumber ?? '').replace(/\D/g, '');
  if (!digits) return '';
  const dial = dialFor(iso) || dialFor(DEFAULT_DIAL_ISO);
  if (dial === '1' && digits.length === 10) {
    return `+1 ${nationalPhone(iso, digits)}`;
  }
  if (!dial) return localNumber;
  return `+${dial} ${nationalPhone(iso, digits)}`;
}
